import { Component, OnInit, OnDestroy, signal, computed, inject, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LeaveService, StaffLeave } from '../../../services/leave.service';
import { StaffService, Staff } from '../../../services/staff';
import { ModalService } from '../../../services/modal.service';

interface CalendarDay {
	date: string;
	dayNumber: number;
	isCurrentMonth: boolean;
	isToday: boolean;
	isPast: boolean;
	leaves: StaffLeave[];
}

@Component({
	selector: 'app-leaves',
	standalone: true,
	imports: [CommonModule, FormsModule, ReactiveFormsModule],
	templateUrl: './leaves.html',
	styleUrl: './leaves.css',
})
export class Leaves implements OnInit, OnDestroy {
	@ViewChild('leaveModalTemplate') leaveModalTemplate!: TemplateRef<any>;

	private leaveService = inject(LeaveService);
	private staffService = inject(StaffService);
	private modalService = inject(ModalService);
	private fb = inject(FormBuilder);

	// Data
	leaves = signal<StaffLeave[]>([]);
	staffList = signal<Staff[]>([]);
	isLoading = signal(true);
	isSubmitting = signal(false);
	errorMessage = signal('');

	// Calendar
	currentMonth = signal(new Date());
	calendarDays = signal<CalendarDay[]>([]);

	// Filter
	selectedStaffFilter = signal<number | null>(null);

	// Form
	leaveForm!: FormGroup;
	editingId = signal<number | null>(null);
	isMultipleDays = signal(false);
	selectedDates = signal<string[]>([]);

	// For template use - pre-computed values
	todayDateString = this.formatDate(new Date());
	futureDates: { date: string; label: string }[] = [];

	// Computed
	currentMonthLabel = computed(() => {
		const date = this.currentMonth();
		return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
	});

	filteredLeaves = computed(() => {
		const staffId = this.selectedStaffFilter();
		if (!staffId) return this.leaves();
		return this.leaves().filter((l) => l.staff_id === staffId);
	});

	ngOnInit() {
		this.initForm();
		this.loadStaff();
		this.loadLeaves();
		this.generateFutureDates();
	}

	generateFutureDates() {
		this.futureDates = [];
		for (let i = 0; i < 30; i++) {
			const date = new Date(Date.now() + i * 24 * 60 * 60 * 1000);
			this.futureDates.push({
				date: this.formatDate(date),
				label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
			});
		}
	}

	ngOnDestroy() {
		this.modalService.close();
	}

	initForm() {
		this.leaveForm = this.fb.group({
			staff_id: ['', Validators.required],
			leave_date: ['', Validators.required],
			leave_type: ['full_day', Validators.required],
			reason: [''],
		});
	}

	loadStaff() {
		this.staffService.getAll().subscribe({
			next: (response) => {
				if (response.status) {
					this.staffList.set(response.data);
				}
			},
		});
	}

	loadLeaves() {
		this.isLoading.set(true);

		// Get leaves for current month
		const currentDate = this.currentMonth();
		const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
		const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

		const filters: any = {
			start_date: this.formatDate(startDate),
			end_date: this.formatDate(endDate),
		};

		if (this.selectedStaffFilter()) {
			filters.staff_id = this.selectedStaffFilter();
		}

		this.leaveService.getAll(filters).subscribe({
			next: (response) => {
				this.isLoading.set(false);
				if (response.status) {
					// Normalize leave dates to YYYY-MM-DD format (remove time/timezone info)
					const normalizedLeaves = response.data.data.map((leave: any) => {
						let dateStr = leave.leave_date;
						// If date contains 'T' (ISO format), extract just the date part
						if (dateStr.includes('T')) {
							dateStr = dateStr.split('T')[0];
						}
						return {
							...leave,
							leave_date: dateStr
						};
					});
					console.log('Leaves loaded:', normalizedLeaves);
					this.leaves.set(normalizedLeaves);
					this.generateCalendar();
				}
			},
			error: (error) => {
				console.error('Error loading leaves:', error);
				this.isLoading.set(false);
			},
		});
	}

	generateCalendar() {
		const currentDate = this.currentMonth();
		const year = currentDate.getFullYear();
		const month = currentDate.getMonth();

		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);
		const startPadding = firstDay.getDay();

		const days: CalendarDay[] = [];
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		
		console.log('Generating calendar for', year, month, 'with', this.leaves().length, 'leaves');

		// Previous month padding
		const prevMonthLastDay = new Date(year, month, 0).getDate();
		for (let i = startPadding - 1; i >= 0; i--) {
			const dayNum = prevMonthLastDay - i;
			const date = new Date(year, month - 1, dayNum);
			days.push({
				date: this.formatDate(date),
				dayNumber: dayNum,
				isCurrentMonth: false,
				isToday: false,
				isPast: date < today,
				leaves: [],
			});
		}

		// Current month days
		for (let day = 1; day <= lastDay.getDate(); day++) {
			const date = new Date(year, month, day);
			const dateStr = this.formatDate(date);
			const leavesOnDay = this.leaves().filter((l) => l.leave_date === dateStr);

			days.push({
				date: dateStr,
				dayNumber: day,
				isCurrentMonth: true,
				isToday: date.getTime() === today.getTime(),
				isPast: date < today,
				leaves: leavesOnDay,
			});
		}

		// Next month padding
		const remainingDays = 42 - days.length; // 6 rows * 7 days
		for (let day = 1; day <= remainingDays; day++) {
			const date = new Date(year, month + 1, day);
			days.push({
				date: this.formatDate(date),
				dayNumber: day,
				isCurrentMonth: false,
				isToday: false,
				isPast: false,
				leaves: [],
			});
		}

		this.calendarDays.set(days);
	}

	prevMonth() {
		const current = this.currentMonth();
		this.currentMonth.set(new Date(current.getFullYear(), current.getMonth() - 1, 1));
		this.loadLeaves();
	}

	nextMonth() {
		const current = this.currentMonth();
		this.currentMonth.set(new Date(current.getFullYear(), current.getMonth() + 1, 1));
		this.loadLeaves();
	}

	onStaffFilterChange(staffId: number | null) {
		this.selectedStaffFilter.set(staffId);
		this.loadLeaves();
	}

	openModal(leave?: StaffLeave) {
		this.errorMessage.set('');
		this.isMultipleDays.set(false);
		this.selectedDates.set([]);

		if (leave) {
			this.editingId.set(leave.id);
			this.leaveForm.patchValue({
				staff_id: leave.staff_id,
				leave_date: leave.leave_date,
				leave_type: leave.leave_type,
				reason: leave.reason || '',
			});
		} else {
			this.editingId.set(null);
			this.leaveForm.reset({
				staff_id: '',
				leave_date: '',
				leave_type: 'full_day',
				reason: '',
			});
		}

		this.modalService.open(this.leaveModalTemplate, '500px', () => {
			this.editingId.set(null);
			this.leaveForm.reset();
		});
	}

	openModalForDate(date: string) {
		if (new Date(date) < new Date(new Date().setHours(0, 0, 0, 0))) {
			return; // Don't allow adding leaves for past dates
		}

		this.errorMessage.set('');
		this.editingId.set(null);
		this.isMultipleDays.set(false);
		this.selectedDates.set([]);

		this.leaveForm.reset({
			staff_id: this.selectedStaffFilter() || '',
			leave_date: date,
			leave_type: 'full_day',
			reason: '',
		});

		this.modalService.open(this.leaveModalTemplate, '500px', () => {
			this.editingId.set(null);
			this.leaveForm.reset();
		});
	}

	closeModal() {
		this.modalService.close();
	}

	saveLeave() {
		if (this.leaveForm.invalid) return;

		this.isSubmitting.set(true);
		this.errorMessage.set('');

		const formValue = this.leaveForm.value;

		if (this.isMultipleDays() && this.selectedDates().length > 0) {
			// Bulk create
			this.leaveService
				.bulkCreate({
					staff_id: formValue.staff_id,
					dates: this.selectedDates(),
					leave_type: formValue.leave_type,
					reason: formValue.reason,
				})
				.subscribe({
					next: (response) => {
						this.isSubmitting.set(false);
						if (response.status) {
							console.log('Bulk leave created:', response.data);
							this.closeModal();
							this.loadLeaves();
						} else {
							this.errorMessage.set(response.message || 'Failed to create leaves');
						}
					},
					error: (error) => {
						console.error('Error creating bulk leaves:', error);
						this.isSubmitting.set(false);
						this.errorMessage.set(error.error?.message || 'Failed to create leaves');
					},
				});
		} else if (this.editingId()) {
			// Update
			this.leaveService.update(this.editingId()!, formValue).subscribe({
				next: (response) => {
					this.isSubmitting.set(false);
					if (response.status) {
						this.closeModal();
						this.loadLeaves();
					} else {
						this.errorMessage.set(response.message || 'Failed to update leave');
					}
				},
				error: (error) => {
					this.isSubmitting.set(false);
					this.errorMessage.set(error.error?.message || 'Failed to update leave');
				},
			});
		} else {
			// Create single
			this.leaveService.create(formValue).subscribe({
				next: (response) => {
					this.isSubmitting.set(false);
					if (response.status) {
						console.log('Leave created:', response.data);
						this.closeModal();
						this.loadLeaves();
					} else {
						this.errorMessage.set(response.message || 'Failed to create leave');
					}
				},
				error: (error) => {
					console.error('Error creating leave:', error);
					this.isSubmitting.set(false);
					this.errorMessage.set(error.error?.message || 'Failed to create leave');
				},
			});
		}
	}

	deleteLeave(leave: StaffLeave) {
		if (!confirm(`Are you sure you want to delete this leave record for ${leave.staff?.full_name}?`)) {
			return;
		}

		this.leaveService.delete(leave.id).subscribe({
			next: (response) => {
				if (response.status) {
					this.closeModal();
					this.loadLeaves();
				}
			},
		});
	}

	deleteCurrentLeave() {
		const id = this.editingId();
		if (!id) return;

		const staffName = this.getSelectedStaffName();
		if (!confirm(`Are you sure you want to delete this leave record for ${staffName}?`)) {
			return;
		}

		this.leaveService.delete(id).subscribe({
			next: (response) => {
				if (response.status) {
					this.closeModal();
					this.loadLeaves();
				}
			},
		});
	}

	getSelectedStaffName(): string {
		const staffId = this.leaveForm.value.staff_id;
		const staff = this.staffList().find((s) => s.id == staffId);
		return staff?.full_name || 'Unknown';
	}

	getStaffFirstName(fullName: string | undefined): string {
		if (!fullName) return '';
		return fullName.split(' ')[0] || '';
	}

	toggleMultipleDays() {
		this.isMultipleDays.set(!this.isMultipleDays());
		if (!this.isMultipleDays()) {
			this.selectedDates.set([]);
		}
	}

	toggleDateSelection(date: string) {
		const current = this.selectedDates();
		const index = current.indexOf(date);
		if (index > -1) {
			this.selectedDates.set(current.filter((d) => d !== date));
		} else {
			this.selectedDates.set([...current, date]);
		}
	}

	isDateSelected(date: string): boolean {
		return this.selectedDates().includes(date);
	}

	getLeaveTypeLabel(type: string): string {
		switch (type) {
			case 'full_day':
				return 'Full Day';
			case 'half_day_morning':
				return 'Morning';
			case 'half_day_afternoon':
				return 'Afternoon';
			default:
				return type;
		}
	}

	getLeaveTypeColor(type: string): string {
		switch (type) {
			case 'full_day':
				return 'bg-red-100 text-red-700';
			case 'half_day_morning':
				return 'bg-orange-100 text-orange-700';
			case 'half_day_afternoon':
				return 'bg-yellow-100 text-yellow-700';
			default:
				return 'bg-gray-100 text-gray-700';
		}
	}

	private formatDate(date: Date): string {
		return date.toISOString().split('T')[0];
	}
}
